/**
 * Global home page on vaultpayday.com — a waitlist until Vault opens in more
 * countries.
 *
 * I keep it honest and country-neutral: nothing here is about one country. It
 * says Vault is opening country by country, in each person's own currency, and
 * promises nothing it can't yet do. The form asks for an email, a country (so
 * each launch follows real demand) and a ticked consent box, and posts to
 * /auth/waitlist/ with source 'global'.
 *
 * Beside the form an example dashboard is drawn from the design system, not a
 * screenshot, and every figure on it is labelled as an example. It follows the
 * country picked in the form: /auth/regions/?country=XX gives that country's
 * currency and locale, and the example amounts are shown in that currency.
 * Existing members sign in through the one Vault platform.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { countryOptions, guessCountry } from '../global.js';
import './vault-home.css';

// The one Vault platform, where existing members sign in.
const SIGN_IN_URL = 'https://vault-app.co.za/login';

// Example figures only (whole units of whatever currency is picked). They show
// the idea: balance minus what is still owed, per bank, is what is really left.
const EX_BANKS = [
  { mono: 'E', name: 'Everyday account', amt: 1275, tone: 'up' },
  { mono: 'S', name: 'Savings', amt: 2600, tone: 'up' },
  { mono: 'C', name: 'Credit card', amt: -180, tone: 'down' },
];
const EX_OWED = [
  { name: 'Rent', meta: 'Everyday account · due on the 1st', amt: 950 },
  { name: 'Groceries', meta: '4 of 8 shops logged', amt: 210 },
  { name: 'Phone', meta: 'paid ✓', amt: 35, paid: true },
];
const EX_BALANCE = 4855;

const POINTS = [
  ['Your month starts on payday', 'Not on the 1st. Vault plans from the day you are paid to the next, and waits if you are paid late.'],
  ['Every bank stays honest', 'Each account shows what it still owes, so a short account can never hide behind a healthy total.'],
  ['In your own currency', 'Amounts, dates and numbers follow your country. You import your own statements or add entries yourself.'],
];

export default function GlobalWaitlist() {
  const options = useMemo(() => countryOptions('en'), []);
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(() => {
    try { return guessCountry(navigator.languages || [navigator.language]); } catch { return ''; }
  });
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [money, setMoney] = useState({ currency: 'USD', locale: 'en-US' });

  const countryName = options.find((o) => o.code === country)?.name || country;

  // The tab title is about Vault, never one country (index.html carries the
  // South African title for vault-app.co.za).
  useEffect(() => {
    const before = document.title;
    document.title = 'Vault — payday-first budgeting, in your own currency';
    return () => { document.title = before; };
  }, []);

  // The example dashboard speaks the picked country's currency. A failed look-up
  // keeps the last one; it is decoration, never a reason to block the form.
  useEffect(() => {
    if (!country) return;
    let on = true;
    api(`/auth/regions/?country=${encodeURIComponent(country)}`)
      .then((d) => {
        if (on && d?.default_currency) {
          setMoney({ currency: d.default_currency, locale: d.default_locale || 'en' });
        }
      })
      .catch(() => {});
    return () => { on = false; };
  }, [country]);

  const fmt = useMemo(() => {
    let f;
    try {
      f = new Intl.NumberFormat(money.locale, { style: 'currency', currency: money.currency, maximumFractionDigits: 0 });
    } catch {
      f = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    }
    return (n) => f.format(n);
  }, [money]);

  const owed = EX_OWED.filter((r) => !r.paid).reduce((s, r) => s + r.amt, 0);

  const join = async (e) => {
    e.preventDefault();
    setErr('');
    if (!country) { setErr('Choose your country from the list.'); return; }
    if (!consent) { setErr('Please tick the consent box to join the waitlist.'); return; }
    setBusy(true);
    try {
      await api('/auth/waitlist/', { method: 'POST', body: { email, country, consent: true, source: 'global' } });
      setDone(true);
    } catch (ex) {
      setErr(ex?.data?.detail || 'Could not save — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vhome vh-page">
      <header className="vh-nav">
        <div className="vh-brand">
          <img src="/logo.svg" alt="" />
          <span className="vh-brand-word">Vault</span>
        </div>
        <div className="vh-navwrap">
          <a href={SIGN_IN_URL} className="vh-btn vh-btn-ghost vh-btn-md">Already a member? Sign in</a>
        </div>
      </header>

      <section className="vh-hero gw-hero">
        <div className="vh-hero-copy">
          <div className="vh-badge">
            <span className="dot" />
            <span>Payday-first budgeting · opening country by country</span>
          </div>
          <h1 className="vh-h1">Know what&rsquo;s left,<br /><span className="accent">in every bank.</span></h1>
          <p className="vh-lead">
            Vault is payday-first budgeting. It subtracts what you still owe from what you
            actually have, per account, all month. Your month starts the day you&rsquo;re paid,
            not on the 1st.
          </p>
          <p className="vh-lead">
            Vault is opening country by country, in your own currency. Join the waitlist and
            you&rsquo;ll get one email when it opens where you live.
          </p>

          {done ? (
            <div className="vh-wait-done" role="status">
              Thanks — you&rsquo;re on the list{countryName ? ` for ${countryName}` : ''}.
            </div>
          ) : (
            <form className="vh-wait vh-wait-global" onSubmit={join} noValidate>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@email.com" className="vh-input" aria-label="Email address"
                     autoComplete="email" inputMode="email" />
              <select className="vh-input" value={country} onChange={(e) => setCountry(e.target.value)}
                      aria-label="Your country" required>
                <option value="">Your country…</option>
                {options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
              </select>
              <label className="vh-tester-check">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>I agree that Vault may email me about its launch in my country. I can ask to be removed at any time.</span>
              </label>
              <button type="submit" className="vh-btn vh-btn-primary vh-btn-lg" disabled={busy}>
                {busy ? 'Saving…' : 'Join the waitlist'}
              </button>
            </form>
          )}
          {err && <div className="vh-wait-err" role="alert">{err}</div>}

          <p className="vh-disclaimer">
            Vault is an independent budgeting app — not a bank, not affiliated with one. It never
            asks for your online-banking login: you import your own statements or add entries yourself.
          </p>
        </div>

        <div className="vh-hero-shot">
          <div className="vh-shot" aria-label="Example Vault dashboard with example figures">
            <div className="vh-chrome">
              <div className="dots"><i /><i /><i /></div>
              <div className="url">Vault · example</div>
            </div>
            <div className="vh-shot-body">
              <div className="vh-shot-head">
                <img src="/logo.svg" alt="" />
                <span className="vh-shot-brand">Vault</span>
                <span className="vh-shot-month">payday to payday</span>
                <span className="gw-example">Example figures · {money.currency}</span>
              </div>

              <div className="vh-leftover">
                <div className="label">Really left across your banks</div>
                <div className="big">{fmt(EX_BALANCE - owed)}</div>
                <div className="sub">balance now {fmt(EX_BALANCE)} · still to pay {fmt(owed)}</div>
              </div>

              <div className="vh-bankgrid">
                {EX_BANKS.map((b) => (
                  <div className="vh-bank" key={b.name}>
                    <div className="row"><span className="mono">{b.mono}</span><span className="name">{b.name}</span></div>
                    <div className={`amt ${b.tone}`}>{fmt(b.amt)}</div>
                    <div className="cap">left now</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="vh-col-h">▼ Still to pay</div>
                <div className="vh-rows">
                  {EX_OWED.map((r) => (
                    <div className="vh-orow" key={r.name}>
                      <span className={`vh-tick ${r.paid ? 'paid' : ''}`} />
                      <div className="body">
                        <div className={`name ${r.paid ? 'paid' : ''}`}>{r.name}</div>
                        <div className="meta">{r.meta}</div>
                      </div>
                      <div className="amt">{fmt(r.amt)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="vh-section gw-points">
        {POINTS.map(([t, d]) => (
          <div className="vh-card" key={t}>
            <div className="gw-point-t">{t}</div>
            <div className="gw-point-d">{d}</div>
          </div>
        ))}
      </section>

      <section className="vh-close">
        <div className="vh-foot">
          Vault · vaultpayday.com · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> · your data is yours to export or delete, any time.
        </div>
        <div className="vh-foot">
          Created by <a href="https://mare-greyling.com" target="_blank" rel="noreferrer">thevertex</a>
        </div>
      </section>
    </div>
  );
}
